<template>
  <div class="mgc-system lynda" v-if="enabled">
    <button
      v-if="!open && available"
      ref="launcher"
      class="mgc-button lynda-launcher"
      @click="show"
    >
      Ask Lynda
      <svg
        class="lynda-action-icon"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M3 8h9M8 4l4 4-4 4" />
      </svg>
    </button>
    <section
      v-if="open"
      ref="panel"
      class="lynda-panel"
      :style="docked ? { width: `${panelWidth}px` } : null"
      id="lynda-panel"
      role="dialog"
      :aria-modal="compact ? 'true' : null"
      aria-labelledby="lynda-title"
      @keydown.esc.stop="close"
      @keydown.tab="containMobileFocus"
    >
      <div
        v-if="docked"
        class="lynda-resizer"
        role="separator"
        tabindex="0"
        aria-label="Resize Ask Lynda panel"
        aria-orientation="vertical"
        aria-controls="lynda-panel"
        :aria-valuemin="360"
        :aria-valuemax="maxPanelWidth"
        :aria-valuenow="panelWidth"
        :aria-valuetext="`${panelWidth} pixels wide`"
        title="Drag to resize, or use Left and Right arrow keys"
        @pointerdown="startResize"
        @pointermove="resizePanel"
        @pointerup="endResize"
        @pointercancel="endResize"
        @lostpointercapture="resizing = false"
        @keydown="resizeWithKeyboard"
      ><span aria-hidden="true"></span></div>
      <header class="lynda-header">
        <div>
          <span class="lynda-eyebrow">Your analytics companion</span>
          <h2 id="lynda-title" ref="title" tabindex="-1">Ask Lynda</h2>
        </div>
        <button
          class="lynda-icon"
          aria-label="Close Lynda and clear chat"
          @click="close"
        >
          ×
        </button>
      </header>
      <div class="lynda-scope">
        <strong>{{ conversationName }}</strong
        ><span>{{ conversationScopeLabel }}</span
        ><span>Surveys &amp; engagement</span>
      </div>
      <div v-if="scopeChanged" class="lynda-scope-change">
        Your dashboard selection has changed. This chat still uses its original
        selection.
        <button @click="newChat">Start with current selection</button>
      </div>
      <div
        ref="conversation"
        class="lynda-conversation"
        :aria-busy="working ? 'true' : 'false'"
      >
        <div v-if="!messages.length" class="lynda-empty">
          <div class="lynda-mark" aria-hidden="true">L</div>
          <h3>What would you like to understand?</h3>
          <p>
            Explore your survey responses and clip engagement. Ask a question,
            then follow the interesting details.
          </p>
          <div class="lynda-suggestions">
            <button
              v-for="question in suggestions"
              :key="question"
              @click="
                draft = question
                focusComposer()
              "
            >
              {{ question }}
              <svg
                class="lynda-action-icon"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M3 8h9M8 4l4 4-4 4" />
              </svg>
            </button>
          </div>
        </div>
        <article
          v-for="(message, i) in messages"
          :key="i"
          :class="['lynda-message', `lynda-message-${message.role}`]"
          :aria-label="
            message.role === 'user' ? 'Your question' : 'Lynda’s answer'
          "
        >
          <span class="lynda-message-author">{{
            message.role === 'user' ? 'You' : 'Lynda'
          }}</span>
          <p v-if="message.role === 'user'">{{ message.content }}</p>
          <template v-else>
            <LyndaAnswer :source="message.content" />
            <LyndaReportAttachment v-for="report in message.reports" :key="report.id" :report="report" :job-id="message.jobId" :download="downloadReport" />
          </template>
        </article>
        <div v-if="busy" class="lynda-progress" role="status">
          <span class="lynda-pulse" aria-hidden="true"></span
          ><span>{{
            progress[progress.length - 1] || 'Starting your investigation…'
          }}</span>
        </div>
        <p v-if="notice" role="status" class="lynda-notice">{{ notice }}</p>
        <div v-if="cancelError" role="alert" class="lynda-error">
          Lynda may still be running. Retry Stop before starting another
          question.
        </div>
        <div v-if="error" role="alert" class="lynda-error">
          <p>{{ error }}</p>
          <button v-if="newChatRequired" @click="newChat" :disabled="working">
            Start a new chat
          </button>
          <button v-else @click="retry" :disabled="working">Retry question</button>
        </div>
        <span class="lynda-sr" aria-live="polite">{{ announcement }}</span>
      </div>
      <form class="lynda-composer" @submit.prevent="send">
        <label for="lynda-question">{{
          messages.length ? 'Ask a follow-up' : 'Your question'
        }}</label>
        <textarea
          id="lynda-question"
          ref="composer"
          v-model="draft"
          class="mgc-input"
          rows="1"
          maxlength="6000"
          :disabled="working"
          placeholder="Ask about your data…"
          @keydown.ctrl.enter.prevent="send"
          @keydown.meta.enter.prevent="send"
        ></textarea>
        <details
          class="lynda-session-info"
          @keydown.esc.stop.prevent="closeSessionInfo"
        >
          <summary aria-label="About this analysis session">
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              aria-hidden="true"
              focusable="false"
            >
              <circle cx="8" cy="8" r="6" />
              <path d="M8 7v4" />
              <circle cx="8" cy="4.5" r="0.7" fill="currentColor" stroke="none" />
            </svg>
          </summary>
          <p>
            Analysis stays available for up to 2 hours. Closing clears this chat.
          </p>
        </details>
        <div class="lynda-composer-actions">
          <button
            v-if="working"
            ref="stop"
            type="button"
            class="mgc-button mgc-button--secondary"
            :disabled="stopping"
            @click="stop"
          >
            {{
              stopping ? 'Stopping…' : cancelError ? 'Retry Stop' : 'Stop'
            }}</button
          ><button
            v-else
            class="mgc-button"
            :disabled="newChatRequired || !draft.trim()"
          >
            Ask
            <svg
              class="lynda-action-icon"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M8 12V3M4 7l4-4 4 4" />
            </svg>
          </button>
        </div>
      </form>
    </section>
  </div>
</template>
<script>
import axios from 'axios'
import LyndaAnswer from './LyndaAnswer.vue'
import LyndaReportAttachment from './LyndaReportAttachment.vue'
import '@/styles/mgc-system.css'
import './lynda.css'
const newTurnId = () => {
  const bytes = window.crypto.getRandomValues(new Uint8Array(16))
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}
const base = () =>
  `${(process.env.VUE_APP_ANALYTICS_API_BASE_URL || '').replace(/\/+$/, '')}/stats/ask-lynda`
const config = () => {
  const token = localStorage.getItem('token') || ''
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} }
}
export default {
  name: 'AskLyndaPanel',
  components: { LyndaAnswer, LyndaReportAttachment },
  props: {
    available: Boolean,
    scope: { type: Object, required: true },
    universityName: String,
    scopeLabel: String,
  },
  data() {
    return {
      compact: typeof window !== 'undefined' && window.innerWidth <= 600,
      viewportWidth: window.innerWidth,
      preferredWidth: 560,
      resizing: false,
      enabled: false,
      open: false,
      draft: '',
      messages: [],
      busy: false,
      progress: [],
      error: '',
      newChatRequired: false,
      notice: '',
      announcement: '',
      jobId: '',
      turnId: '',
      failedRequest: null,
      pendingCancellation: '',
      creating: false,
      stopping: false,
      cancelError: false,
      generation: 0,
      timer: null,
      conversationScope: null,
      conversationName: '',
      conversationScopeLabel: '',
      failedQuestion: '',
      suggestions: [
        'How does engagement compare across ceremonies?',
        'Which survey questions have the biggest differences?',
        'What should I look into next?',
      ],
    }
  },
  computed: {
    docked() {
      return this.viewportWidth >= 1024
    },
    maxPanelWidth() {
      return Math.min(800, this.viewportWidth - 640)
    },
    panelWidth() {
      return Math.min(this.preferredWidth, this.maxPanelWidth)
    },
    layoutWidth() {
      return this.enabled && this.open && this.docked ? this.panelWidth : 0
    },
    working() {
      return (
        this.busy ||
        this.creating ||
        this.stopping ||
        Boolean(this.pendingCancellation)
      )
    },
    scopeChanged() {
      return (
        JSON.stringify(this.scope) !== JSON.stringify(this.conversationScope)
      )
    },
  },
  watch: {
    layoutWidth(width) {
      this.$emit('layout-width', width)
    },
    draft() {
      this.$nextTick(this.resizeComposer)
    },
    'scope.universitySlug'() {
      if (this.open) this.close()
    },
  },
  async mounted() {
    window.addEventListener('resize', this.updateCompact)
    try {
      const { data } = await axios.get(`${base()}/capabilities`, config())
      this.enabled = data.enabled === true
    } catch (_) {
      this.enabled = false
    }
  },
  beforeDestroy() {
    this.$emit('layout-width', 0)
    if (typeof window !== 'undefined')
      window.removeEventListener('resize', this.updateCompact)
    this.cancelRun()
  },
  methods: {
    startResize(event) {
      if (event.button !== 0) return
      event.preventDefault()
      event.currentTarget.focus()
      event.currentTarget.setPointerCapture(event.pointerId)
      this.resizing = true
    },
    resizePanel(event) {
      if (this.resizing) this.setPanelWidth(this.viewportWidth - event.clientX)
    },
    endResize(event) {
      this.resizing = false
      if (event.currentTarget.hasPointerCapture(event.pointerId))
        event.currentTarget.releasePointerCapture(event.pointerId)
    },
    setPanelWidth(width) {
      this.preferredWidth = Math.round(Math.max(360, Math.min(this.maxPanelWidth, width)))
      this.$nextTick(this.resizeComposer)
    },
    resizeWithKeyboard(event) {
      const widths = {
        ArrowLeft: this.panelWidth + 24,
        ArrowRight: this.panelWidth - 24,
        Home: 360,
        End: this.maxPanelWidth,
      }
      if (!(event.key in widths)) return
      event.preventDefault()
      this.setPanelWidth(widths[event.key])
    },
    updateCompact() {
      this.viewportWidth = window.innerWidth
      this.compact = window.innerWidth <= 600
      this.resizeComposer()
    },
    containMobileFocus(event) {
      if (!this.compact) return
      const controls = [
        ...this.$refs.panel.querySelectorAll(
          'button:not([disabled]), textarea:not([disabled]), summary, [tabindex="0"]'
        ),
      ].filter((el) => el.getClientRects().length > 0)
      const target = event.shiftKey
        ? controls[controls.length - 1]
        : controls[0]
      if (
        !controls.includes(event.target) ||
        (event.shiftKey && event.target === controls[0]) ||
        (!event.shiftKey && event.target === controls[controls.length - 1])
      ) {
        event.preventDefault()
        target.focus()
      }
    },
    focusStop() {
      this.$nextTick(() => {
        const target = this.stopping ? this.$refs.title : this.$refs.stop
        if (target) target.focus()
      })
    },
    resizeComposer() {
      const input = this.$refs.composer
      if (!input) return
      input.style.height = '44px'
      input.style.height = `${Math.min(150, input.scrollHeight + 2)}px`
      input.style.overflowY = input.scrollHeight + 2 > 150 ? 'auto' : 'hidden'
    },
    closeSessionInfo(event) {
      event.currentTarget.open = false
      event.currentTarget.querySelector('summary').focus()
    },
    focusComposer() {
      this.$nextTick(() => {
        this.resizeComposer()
        if (this.$refs.composer) this.$refs.composer.focus()
      })
    },
    show() {
      this.open = true
      this.reset()
      this.focusComposer()
    },
    reset() {
      this.cancelRun()
      this.messages = []
      this.draft = ''
      this.error = ''
      this.newChatRequired = false
      this.notice = ''
      this.progress = []
      this.failedQuestion = ''
      this.failedRequest = null
      this.turnId = ''
      this.announcement = ''
      this.conversationScope = JSON.parse(JSON.stringify(this.scope))
      this.conversationName = this.universityName
      this.conversationScopeLabel = this.scopeLabel
    },
    newChat() {
      const question = this.newChatRequired ? this.draft : ''
      this.reset()
      this.draft = question
      this.focusComposer()
    },
    close() {
      this.reset()
      this.open = false
      this.$nextTick(() => {
        if (this.$refs.launcher) this.$refs.launcher.focus()
      })
    },
    async cancelJob(id) {
      if (!id) return
      this.pendingCancellation = id
      this.stopping = true
      this.cancelError = false
      try {
        await axios
          .delete(`${base()}/jobs/${encodeURIComponent(id)}`, config())
          .catch((err) => {
            if (![404, 410].includes(err.response?.status)) throw err
          })
        if (this.pendingCancellation === id) {
          this.pendingCancellation = ''
          this.notice = 'Investigation stopped. You can ask another question.'
        }
      } catch (_) {
        this.cancelError = true
        this.notice = ''
      } finally {
        this.stopping = false
        this.busy = false
        if (this.pendingCancellation) this.focusStop()
        else this.focusComposer()
      }
    },
    cancelRun() {
      this.generation++
      clearTimeout(this.timer)
      this.busy = false
      this.creating = false
      const id = this.jobId || this.pendingCancellation
      this.jobId = ''
      if (id && !this.stopping) this.cancelJob(id)
    },
    stop() {
      if (this.stopping) return
      this.notice = 'Requesting a stop…'
      this.cancelRun()
      this.focusStop()
    },
    scrollAnswer() {
      this.$nextTick(() => {
        const el = this.$refs.conversation
        if (!el) return
        const answers = el.querySelectorAll('.lynda-message-assistant')
        const answer = answers[answers.length - 1]
        if (answer)
          el.scrollTop +=
            answer.getBoundingClientRect().top -
            el.getBoundingClientRect().top -
            12
      })
    },
    scrollLatest() {
      this.$nextTick(() => {
        const el = this.$refs.conversation
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    retry() {
      this.draft = this.failedQuestion
      this.send(true)
    },
    async send(retry = false) {
      if (this.working || this.newChatRequired || !this.draft.trim()) return
      const question = this.draft.trim()
      const history = this.messages.map(({ role, content }) => ({ role, content }))
      if (retry === true && history[history.length - 1]?.role === 'user')
        history.pop()
      else this.messages.push({ role: 'user', content: question })
      this.busy = true
      this.creating = true
      this.error = ''
      this.notice = ''
      this.announcement = ''
      this.progress = []
      this.failedQuestion = question
      this.draft = ''
      const request =
        retry === true && this.jobId && this.failedRequest
          ? this.failedRequest
          : {
              ...(this.jobId ? { job_id: this.jobId } : { messages: history }),
              turn_id: newTurnId(),
              question,
              scope: this.conversationScope,
            }
      this.failedRequest = request
      this.turnId = request.turn_id
      const requestedJobId = request.job_id || `ask-lynda-${request.turn_id}`
      this.jobId = requestedJobId
      const generation = ++this.generation
      this.scrollLatest()
      this.focusStop()
      try {
        const { data } = await axios.post(
          `${base()}/jobs`,
          request,
          config()
        )
        if (generation !== this.generation) return
        this.creating = false
        if (data.job_id !== requestedJobId || data.turn_id !== request.turn_id)
          throw new Error('Unexpected investigation')
        this.poll(generation)
      } catch (err) {
        if (generation !== this.generation) return
        this.creating = false
        this.fail(err)
      }
    },
    fail(err, active = false) {
      this.busy = false
      const status = err.response?.status
      if (status === 410) this.jobId = ''
      if (active && this.jobId && status !== 409) {
        this.pendingCancellation = this.jobId
        this.notice = 'Stop this investigation before retrying the question.'
      }
      this.newChatRequired =
        status === 409 ||
        status === 410 ||
        (status === 400 &&
          (err.response?.data?.code === 'conversation_too_long' ||
            (err.response?.data?.code === 'request_too_large' && this.messages.length > 1)))
      this.error =
        status === 410
          ? 'This analysis session has ended. Start a new chat to load fresh data and keep your question.'
          : status === 409
            ? 'This analysis session cannot continue. Start a new chat to keep working with your question.'
            : this.newChatRequired
              ? 'This chat is too large to continue. Starting a new chat clears earlier answers and keeps your question.'
              : status === 403
                ? 'Access to Ask Lynda or this data is no longer available.'
                : 'Lynda could not finish this question. Your earlier answers are still here.'
      this.draft = this.failedQuestion
      this.scrollLatest()
      if (this.pendingCancellation) this.focusStop()
      else this.focusComposer()
    },
    async downloadReport(jobId, reportId) {
      const { data } = await axios.get(`${base()}/jobs/${encodeURIComponent(jobId)}`, {
        ...config(), params: { report_id: reportId },
      })
      return data.url
    },
    async poll(generation) {
      try {
        const { data } = await axios.get(
          `${base()}/jobs/${encodeURIComponent(this.jobId)}`,
          config()
        )
        if (generation !== this.generation) return
        if (data.turn_id !== this.turnId) {
          this.timer = setTimeout(() => this.poll(generation), 1500)
          return
        }
        this.progress = Array.isArray(data.progress)
          ? data.progress.filter((s) => typeof s === 'string')
          : []
        if (data.status === 'completed') {
          if (
            !data.answer ||
            typeof data.answer.mdma !== 'string' ||
            !data.answer.mdma.trim()
          )
            throw new Error('Missing answer')
          this.messages.push({ role: 'assistant', content: data.answer.mdma, reports: data.answer.reports || [], jobId: this.jobId })
          this.busy = false
          this.announcement = 'Lynda’s answer is ready.'
          this.scrollAnswer()
          this.focusComposer()
        } else if (data.status === 'cancelled') {
          this.jobId = ''
          this.busy = false
          this.notice = 'Investigation stopped. You can ask another question.'
        } else if (data.status === 'failed') {
          this.jobId = ''
          throw new Error('Investigation failed')
        } else this.timer = setTimeout(() => this.poll(generation), 1500)
      } catch (err) {
        if (generation === this.generation) this.fail(err, true)
      }
    },
  },
}
</script>
